import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import {
  DropdownOption,
  EnumMarcaCategoria,
} from '@core/models/models';
import { STATIC_SOURCES } from '@shared/constants/dropdown-static-data';

export const PROPRIEDADE_OPCOES_SOURCE_KEY = 'PROPRIEDADE_OPCOES';

interface DynamicSourceConfig {
  endpoint: string;
  buildParams?: (dependValue: any) => Record<string, string>;
  labelField: string;
  valueField: string;
  isPaginated?: boolean;
  filterByDependField?: string;
}

const marcaSource = (categoria: EnumMarcaCategoria): DynamicSourceConfig => ({
  endpoint: '/api/Marcas',
  labelField: 'nome',
  valueField: 'id',
  isPaginated: true,
  buildParams: dependValue => ({
    categoria: String(dependValue ?? categoria),
    pageSize: '200',
  }),
});

const DYNAMIC_SOURCES: Record<string, DynamicSourceConfig> = {
  TIPOS_ARMA: {
    endpoint: '/api/Enum/TipoArma',
    labelField: 'nome',
    valueField: 'nome',
  },
  MARCAS_ARMA: marcaSource(EnumMarcaCategoria.ARMA),
  MARCAS_VEICULO: marcaSource(EnumMarcaCategoria.VEICULO),
  MARCAS_UTENSILIO: marcaSource(EnumMarcaCategoria.UTENSILIO),
  MODELOS_POR_MARCA: {
    endpoint: '/api/Modelos',
    labelField: 'nome',
    valueField: 'id',
    isPaginated: true,
    buildParams: marcaId => ({ marcaId: String(marcaId), pageSize: '200' }),
  },
  ESTADOS_ARMA: {
    endpoint: '/api/Enum/EstadoArma',
    labelField: 'nome',
    valueField: 'codigo',
  },
  ESTADOS_VEICULO: {
    endpoint: '/api/Enum/EstadoVeiculo',
    labelField: 'nome',
    valueField: 'codigo',
  },
  TIPOS_COMBUSTIVEL: {
    endpoint: '/api/Enum/TipoCombustivel',
    labelField: 'nome',
    valueField: 'codigo',
  },
  CATEGORIAS_UTENSILIO: {
    endpoint: '/api/Enum/CategoriaUtensilio',
    labelField: 'nome',
    valueField: 'codigo',
  },
  ESTADOS_UTENSILIO: {
    endpoint: '/api/Enum/EstadoUtensilio',
    labelField: 'nome',
    valueField: 'codigo',
  },
  POSTOS: {
    endpoint: '/api/Postos',
    labelField: 'nome',
    valueField: 'id',
    isPaginated: true,
    buildParams: () => ({ pageSize: '200' }),
  },
  EFETIVOS: {
    endpoint: '/api/Efetivos',
    labelField: 'nome',
    valueField: 'id',
    isPaginated: true,
    buildParams: () => ({ pageSize: '200' }),
  },
  [PROPRIEDADE_OPCOES_SOURCE_KEY]: {
    endpoint: '/api/PropriedadeOpcoes',
    labelField: 'valor',
    valueField: 'valor',
    isPaginated: true,
    buildParams: propriedadeId => ({ propriedadeId: String(propriedadeId), pageSize: '100' }),
    filterByDependField: 'propriedadeId',
  },
};

@Injectable({ providedIn: 'root' })
export class DataSourceRegistryService {
  private readonly http = inject(HttpClient);
  private readonly cache = new Map<string, Observable<DropdownOption[]>>();

  resolve(
    sourceKey: string,
    dependValue?: any,
    useCache = false,
  ): Observable<DropdownOption[]> {
    if (STATIC_SOURCES[sourceKey]) {
      return of(STATIC_SOURCES[sourceKey]);
    }

    const config = DYNAMIC_SOURCES[sourceKey];
    if (!config) {
      console.warn(`[DataSourceRegistry] sourceKey desconhecida: "${sourceKey}"`);
      return of([]);
    }

    const cacheKey = `${sourceKey}:${dependValue ?? ''}`;
    if (useCache && this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }

    const params = config.buildParams
      ? config.buildParams(dependValue)
      : undefined;
    const url = `${environment.apiurl}${config.endpoint}`;

    const request$ = this.http.get<any>(url, { params }).pipe(
      map(res => this.normaliseResponse(res, config, dependValue)),
      shareReplay(1),
    );

    if (useCache) this.cache.set(cacheKey, request$);
    return request$;
  }

  createOption(
    sourceKey: string,
    dependValue: number,
    name: string,
  ): Observable<DropdownOption[]> {
    const nome = name.trim();

    if (sourceKey.startsWith('MARCAS_')) {
      return this.http
        .post(`${environment.apiurl}/api/Marcas/SalvarMarca`, {
          marcaCategoriaEnum: Number(dependValue),
          name: [nome],
        })
        .pipe(
          tap(() => this.invalidate('MARCAS_')),
          switchMap(() => this.resolve(sourceKey, dependValue, false)),
        );
    }

    if (sourceKey === 'MODELOS_POR_MARCA') {
      return this.http
        .post(`${environment.apiurl}/api/Modelos/SaveModelo`, {
          marcaId: Number(dependValue),
          name: [nome],
        })
        .pipe(
          tap(() => this.invalidate('MODELOS_POR_MARCA')),
          switchMap(() => this.resolve(sourceKey, dependValue, false)),
        );
    }

    if (sourceKey === PROPRIEDADE_OPCOES_SOURCE_KEY) {
      return this.http
        .post(`${environment.apiurl}/api/PropriedadeOpcoes`, {
          propriedadeId: Number(dependValue),
          valor: nome,
        })
        .pipe(
          tap(() => this.invalidate(PROPRIEDADE_OPCOES_SOURCE_KEY)),
          switchMap(() =>
            this.resolve(sourceKey, dependValue, false),
          ),
        );
    }

    return of([]);
  }

  private invalidate(sourceKeyPrefix: string): void {
    for (const key of this.cache.keys()) {
      if (key.startsWith(sourceKeyPrefix)) this.cache.delete(key);
    }
  }

  private normaliseResponse(
    res: any,
    config: DynamicSourceConfig,
    dependValue?: any,
  ): DropdownOption[] {
    let list: any[];

    if (config.isPaginated) {
      list = Array.isArray(res?.data) ? res.data : [];
    } else if (Array.isArray(res)) {
      list = res;
    } else if (Array.isArray(res?.data)) {
      list = res.data;
    } else if (res?.data && typeof res.data === 'object') {
      list = Object.entries(res.data).map(([codigo, nome]) => ({ codigo, nome }));
    } else {
      list = [];
    }

    if (config.filterByDependField && dependValue != null) {
      list = list.filter(item =>
        String(item?.[config.filterByDependField!]) === String(dependValue),
      );
    }

    return list.map(item => ({
      label: String(item[config.labelField] ?? ''),
      value: item[config.valueField],
    }));
  }
}
